import React, { useState } from 'react';
import axios from 'axios';
import { Edit2, Trash2, Plus, Save, X } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useToast } from '../../components/ui/Toast';
import { slugify, getSubcategorySlug } from '../../utils/slugify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';


const SubcategoriesCRUD = ({ subcategories, categories, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    slug: ''
  });

  // Initialize toast and escape key hook
  const { toast } = useToast();
  useEscapeKey(() => setShowModal(false), showModal);

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const openCreateModal = () => {
    setEditingSubcategory(null);
    setSlugManuallyEdited(false);
    setFormData({ name: '', description: '', category_id: '', slug: '' });
    setShowModal(true);
  };

  const openEditModal = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSlugManuallyEdited(true);
    setFormData({
      name: subcategory.name || '',
      description: subcategory.description || '',
      category_id: subcategory.category_id || '',
      slug: subcategory.slug || ''
    });
    setShowModal(true);
  };

  const handleNameChange = (name) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: slugManuallyEdited ? prev.slug : slugify(name)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (editingSubcategory) {
        const subcategoryId = editingSubcategory.id;
        if (!subcategoryId) {
          toast.error('Subcategory ID is missing. Please refresh and try again.');
          return;
        }
        await axios.put(`${API_BASE_URL}/api/admin/subcategories/${subcategoryId}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Subcategory updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/subcategories`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Subcategory created successfully!');
      }
      setShowModal(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      toast.error('Error saving subcategory. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (subcategory) => {
    const subcategoryId = subcategory?.id;
    if (!subcategoryId) {
      toast.error('Subcategory ID is missing. Please refresh and try again.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this subcategory? This will also delete all related agents.')) {
      setIsSubmitting(true);
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/subcategories/${subcategoryId}`, {
          headers: getAuthHeaders()
        });
        toast.success('Subcategory deleted successfully!');
        onUpdate();
      } catch (error) {
        console.error('Error deleting subcategory:', error);
        toast.error('Error deleting subcategory. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subcategories Management</h2>
          <p className="text-gray-600">Organize agents within categories</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <span>+</span>
          <span>Add Subcategory</span>
        </button>
      </div>

      {/* Subcategories Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subcategories.map((subcategory) => (
              <tr key={subcategory.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{subcategory.name}</div>
                  <div className="text-xs text-gray-400">/{getSubcategorySlug(subcategory)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {getCategoryName(subcategory.category_id)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{subcategory.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(subcategory.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => openEditModal(subcategory)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(subcategory)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingSubcategory ? 'Edit Subcategory' : 'Create New Subcategory'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug (optional)</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => {
                      setSlugManuallyEdited(true);
                      setFormData({ ...formData, slug: e.target.value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder={editingSubcategory ? getSubcategorySlug(editingSubcategory) : 'auto-generated-from-name'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Browse URL path: /{formData.slug ? slugify(formData.slug) : (formData.name ? slugify(formData.name) : 'subcategory-slug')}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{editingSubcategory ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      <span>{editingSubcategory ? 'Update' : 'Create'} Subcategory</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default SubcategoriesCRUD;
